// Display formatting shared by every customer-facing page.
//
// Dates: toLocaleDateString() with no locale follows the browser, so a
// browser set to US English showed an Indian customer "7/17/2027" and a
// mm/dd picker. Dates are written out with a named month instead, which no
// reader can misread in either order.
//
// Labels: status values are database enums (active_paid, lead_captured,
// clear, check). None of them may reach the screen as-is; route every one
// through displayLabel().

const DATE = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
});
const TIME = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' });

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "17 Jul 2027", or the fallback for a missing or unparseable value. */
export const formatDate = (value, fallback = '—') => {
  const date = toDate(value);
  return date ? DATE.format(date) : fallback;
};

/** "17 Jul 2027, 3:45 pm". */
export const formatDateTime = (value, fallback = '—') => {
  const date = toDate(value);
  return date ? DATE_TIME.format(date) : fallback;
};

/** "3:45 pm". */
export const formatTime = (value, fallback = '') => {
  const date = toDate(value);
  return date ? TIME.format(date) : fallback;
};

// Values whose plain reading is not just the words in the enum.
const LABELS = {
  // Subscription
  active_paid: 'Active',
  active: 'Active',
  trial: 'Trial',
  trialing: 'Trial',
  past_due: 'Payment overdue',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  expired: 'Expired',
  // Payments
  captured: 'Paid',
  paid: 'Paid',
  // Not "not completed": a payment the gateway took can sit in created until
  // its webhook or reconciliation lands. Never tell someone who paid that
  // nothing was charged.
  created: 'Payment not yet confirmed',
  creating: 'Payment not yet confirmed',
  failed: 'Failed',
  refunded: 'Refunded',
  // Conversation outcomes
  lead_captured: 'Lead captured',
  bounced: 'Left without asking',
  dropped_qualification: 'Left before sharing details',
  escalated: 'Asked for a person',
  abandoned: 'Stopped replying',
  // Attention queue
  clear: 'All clear',
  check: 'Needs attention',
  // Workspace roles
  owner: 'Owner',
  admin: 'Admin',
  member: 'Team member',
  // Tickets and leads
  in_progress: 'In progress',
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  lost: 'Lost',
  open: 'Open',
  resolved: 'Resolved',
  closed: 'Closed',
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/**
 * A readable label for a stored value: the table above first, then
 * snake_case or kebab-case turned into a sentence ("follow_up" -> "Follow up").
 */
export const displayLabel = (value, fallback = '—') => {
  if (value === null || value === undefined || value === '') return fallback;
  const key = String(value).trim();
  const known = LABELS[key.toLowerCase()];
  if (known) return known;
  const words = key.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : fallback;
};

/** "1 message", "2 messages", "1 collection", "3 entries". */
export const plural = (count, singular, pluralForm = `${singular}s`) => {
  const n = Number(count) || 0;
  return `${n.toLocaleString('en-IN')} ${n === 1 ? singular : pluralForm}`;
};
